import { Table, Button, Space, Tag } from '@/shared/ui/primitives';
import { EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/shared/utils/formatters';
import type { ColumnsType } from '@/shared/ui/subpath-types';

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  onEdit: (id: number) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function DistrictTable({ data, loading, sortField, sortDir, onSort, onEdit, onDelete, page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, sortOrder: sortField === 'id' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null },
    { title: t('districts.nameEn'), dataIndex: 'nameEn', sorter: true, sortOrder: sortField === 'nameEn' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null },
    { title: t('districts.nameAr'), dataIndex: 'nameAr', sorter: true, sortOrder: sortField === 'nameAr' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null },
    { title: t('districts.city'), dataIndex: 'cityNameEn', sorter: true, sortOrder: sortField === 'cityNameEn' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null },
    { title: t('districts.sortIndex'), dataIndex: 'sortIndex', sorter: true, sortOrder: sortField === 'sortIndex' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null },
    {
      title: t('common.status'), dataIndex: 'isActive', sorter: true,
      sortOrder: sortField === 'isActive' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (v: boolean) => v ? <Tag icon={<CheckCircleOutlined />} color="success">{t('common.active')}</Tag> : <Tag icon={<CloseCircleOutlined />} color="error">{t('common.inactive')}</Tag>,
    },
    { title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true, sortOrder: sortField === 'createdAt' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null, render: (v: string) => formatDateTime(v) },
    {
      title: t('common.actions'),
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => onEdit(record.id)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Space>
      ),
    },
  ];

  const handleChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter?.field) onSort(sorter.field as string);
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showSizeChanger: false,
        showTotal: (t) => `${t} items`,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
